import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { Button, Jumbo, Separator, Tabs } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import Authentication from './Authentication';
import Cycles from './Cycles';
import JobPositions from './job-positions/JobPositions';
import PersonalInformation from './PersonalInformation';
import Roles from './roles/Roles';
import SentEmails from './SentEmails';

import { ArchiveIcon, CircleUserIcon } from '@treams/treams-icons';

const Background = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	height: 100%;
`;

export default function EmployeeSettings(props: { id: string }) {
	const { t } = useTranslation();

	const query = graphql.useUserQuery({ variables: { id: props.id }, fetchPolicy: 'network-only' });

	const [archive] = graphql.useArchiveUserMutation();
	const [restore] = graphql.useRestoreUserMutation();

	const identityProviders = graphql.useIdentityProvidersQuery();

	const [tab, setTab] = useState(0);

	return (
		<Background>
			<Box backgroundColor="white">
				<Jumbo
					title={t('settings.employees.edit.title', { name: query.data?.user.firstName })}
					button={
						<Flex>
							<Box>
								<Button
									mr={2}
									type="card"
									title={t('employee.settings.buttons.profile')}
									icon={<CircleUserIcon />}
									link={`/users/${props.id}/journey`}
								/>
							</Box>
							<Box>
								{query.data?.user.isArchived ? (
									<Button
										type="secondary"
										title={t('common.restore')}
										onClick={async () => {
											await restore({ variables: { input: { id: query.data!.user.id } } });
										}}
									/>
								) : (
									<Button
										type="destructive"
										title={t('common.archive')}
										icon={<ArchiveIcon />}
										onClick={async () => {
											await archive({ variables: { input: { id: query.data!.user.id } } });
										}}
									/>
								)}
							</Box>
						</Flex>
					}
				/>
			</Box>
			<Separator />
			<Box p={4}>
				<Box maxWidth={800} mx="auto">
					<Flex mb={5}>
						<Box flexGrow={1} flexShrink={1} flexBasis={0} mr={4}>
							<PersonalInformation user={query.data?.user as graphql.User} />
						</Box>
						<Box flexGrow={1} flexShrink={1} flexBasis={0}>
							<Authentication
								user={query.data?.user as graphql.User}
								identityProviders={
									identityProviders.data?.identityProviders as graphql.IdentityProvider[]
								}
							/>
						</Box>
					</Flex>
					<Tabs.Inline mb={3}>
						<Tabs.Inline.Item px={4} py={3} onClick={() => setTab(0)} selected={tab === 0}>
							{t('settings.employees.cycles.title')}
						</Tabs.Inline.Item>
						<Tabs.Inline.Item px={4} py={3} onClick={() => setTab(1)} selected={tab === 1}>
							{t('settings.employees.jobPositions.title')}
						</Tabs.Inline.Item>
						<Tabs.Inline.Item px={4} py={3} onClick={() => setTab(2)} selected={tab === 2}>
							{t('settings.employees.roles.title')}
						</Tabs.Inline.Item>
						<Tabs.Inline.Item px={4} py={3} onClick={() => setTab(3)} selected={tab === 3}>
							{t('settings.employees.sentEmails.title')}
						</Tabs.Inline.Item>
					</Tabs.Inline>
					{tab === 0 && <Cycles id={props.id} />}
					{tab === 1 && <JobPositions id={props.id} />}
					{tab === 2 && <Roles id={props.id} />}
					{tab === 3 && <SentEmails id={props.id} />}
				</Box>
			</Box>
		</Background>
	);
}
